/**
 * WebSocket Manager for Tower CTF
 * 
 * Manages dynamic subscription to WebSocket groups via HTMX
 * Elements automatically subscribe using data-* attributes
 * 
 * @example
 * <!-- Subscription to instance start -->
 * <div data-ws-subscribe="instance_start" data-ws-challenge-id="123">
 *   Real-time updated content
 * </div>
 * 
 * <!-- Subscription to deployment -->
 * <div data-ws-subscribe="challenge_deploy" data-ws-challenge-id="456">
 *   Deployment status
 * </div>
 * 
 * <!-- Subscription to notifications -->
 * <div data-ws-subscribe="notifications">
 *   Notifications list
 * </div>
 */

import {showToast} from "../utils";

class WebSocketManager {
    constructor() {
        this.connected = false;
        this.socket = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
        this.subscriptions = new Map(); // Track active subscriptions
        this.pendingSubscriptions = []; // Queue for subscriptions before connection
        this.mutationObserver = null;
    }

    /**
     * Initialize the WebSocket manager
     */
    init() {
        console.debug('[WebSocket] Initializing manager...');
        
        if (typeof htmx === 'undefined') {
            console.error('[WebSocket] HTMX not available!');
            return;
        }

        this.setupEventListeners();
        this.scanAndSubscribe();
        this.observeDOMChanges();
        this.startHeartbeat();

        console.debug('[WebSocket] Manager successfully initialized');
    }

    /**
     * Configure HTMX WebSocket event listeners
     */
    setupEventListeners() {

        // Connection established
        document.body.addEventListener('htmx:wsOpen', (event) => {
            console.debug('[WebSocket] ✓ Connection established');
            this.connected = true;
            this.reconnectAttempts = 0;
            this.socket = event.detail.socketWrapper;
            this.processPendingSubscriptions();
        });

        // Connection closed
        document.body.addEventListener('htmx:wsClose', () => {
            console.debug('[WebSocket] ✗ Connection closed');
            this.connected = false;
            this.subscriptions.clear();
            this.attemptReconnect();
        });

        // WebSocket error
        document.body.addEventListener('htmx:wsError', (event) => {
            console.error('[WebSocket] Error:', event.detail);
            showToast('Unable to reach the server.', 'danger');
        });

        // Message received (before HTMX processing)
        document.body.addEventListener('htmx:wsBeforeMessage', (event) => {
            const message = event.detail.message;
            if (message.trim().charAt(0) !== '<') {
                event.preventDefault();
                this.handleMessage(message);
            }
        });
    }

    /**
     * Handle messages received from the server
     * @param {string} message - JSON or HTML message
     */
    handleMessage(message) {
        const data = JSON.parse(message);

        switch(data.type) {
            case 'subscription_success':
                console.debug(`[WebSocket] ✓ Subscribed to ${data.group_type}`);
                break;

            case 'unsubscription_success':
                console.debug(`[WebSocket] ✓ Unsubscribed from ${data.group_type}`);
                break;

            case 'error':
                console.error(`[WebSocket] Server error: ${data.message}`);
                showToast(data.message, 'danger');
                break;

            case 'pong':
                console.debug('[WebSocket] Pong received');
                break;

            case 'alpine_update':
                console.debug('[WebSocket] Alpine.js update received');
                this.handleAlpineUpdate(data);
                break;

            case 'redirect':
                const { method, url } = data;
                htmx.ajax(method, url, {
                  target: "#mainContent",
                  select: "#mainContent",
                  swap: "innerHTML",
                  push: url
                });
                break;

            default:
                console.debug('[WebSocket] Unknown control message:', data.type);
        }
    }

    /**
     * Handle Alpine.js data updates
     * @param {Object} data - Update payload
     */
    handleAlpineUpdate(data) {

        const { target, component, updates, method, args } = data;

        if (!target) {
            console.error('[WebSocket] Alpine update: missing target', data);
            return;
        }

        const element = document.querySelector(target);
        if (!element) {
            console.warn(`[WebSocket] Alpine update: element ${target} not found`);
            return;
        }

        if (!element._x_dataStack || element._x_dataStack.length === 0) {
            console.warn(`[WebSocket] Alpine update: no Alpine component on ${target}`);
            return;
        }

        const alpineData = element._x_dataStack[0];

        // Case 1: Method call
        if (method) {
            if (typeof alpineData[method] === 'function') {
                console.debug(`[WebSocket] Alpine: calling ${target}.${method}()`, args);
                alpineData[method](...(args || []));
            } else {
                console.error(`[WebSocket] Alpine: method ${method} not found on ${target}`);
            }
            return;
        }

        // Case 2: Property update
        if (updates && typeof updates === 'object') {
            console.debug(`[WebSocket] Alpine: updating ${target}`, updates);

            Object.keys(updates).forEach(key => {
                if (key in alpineData) {
                    alpineData[key] = updates[key];
                } else {
                    console.warn(`[WebSocket] Alpine: property ${key} not found on ${target}`);
                }
            });
        }
    }

    /**
     * Scan the DOM to find and subscribe elements
     */
    scanAndSubscribe() {
        const elements = document.querySelectorAll('[data-ws-subscribe]');
        console.debug(`[WebSocket] ${elements.length} element(s) found for subscription`);
        elements.forEach(element => this.subscribeElement(element));
    }

    /**
     * Extract subscription data from an element
     */
    subscribeData(element) {
        const groupType = element.dataset.wsSubscribe;

        if (!groupType) {
            console.warn('[WebSocket] Empty data-ws-subscribe attribute', element);
            return;
        }

        const params = this.extractParams(element);
        const subscriptionKey = this.getSubscriptionKey(groupType, params);

        return { groupType, params, subscriptionKey };
    }

    /**
     * Subscribe a specific element
     */
    subscribeElement(element) {
        const data = this.subscribeData(element);

        if (this.subscriptions.has(data.subscriptionKey)) {
            console.debug(`[WebSocket] Already subscribed to ${data.subscriptionKey}`);
            return;
        }

        this.subscriptions.set(data.subscriptionKey, data);
        this.subscribe(data.groupType, data.params);
    }

    /**
     * Unsubscribe a specific element
     */
    unsubscribeElement(element) {
        const data = this.subscribeData(element);

        if (!this.subscriptions.has(data.subscriptionKey)) {
            console.debug(`[WebSocket] Not subscribed to ${data.subscriptionKey}`);
            return;
        }

        this.unsubscribe(data.groupType, data.params);
    }

    /**
     * Dynamically extract parameters from data-ws-* attributes
     */
    extractParams(element) {
        const params = {};

        // Iterate through all data-ws-* attributes (except data-ws-subscribe)
        Array.from(element.attributes).forEach(attr => {
            if (attr.name.startsWith('data-ws-') && attr.name !== 'data-ws-subscribe') {

                const paramName = attr.name
                    .replace('data-ws-', '')
                    .replace(/-/g, '_');

                const value = attr.value;
                params[paramName] = isNaN(value) ? value : parseInt(value, 10);
            }
        });

        return params;
    }

    /**
     * Generate a unique subscription key
     */
    getSubscriptionKey(groupType, params) {
        const sortedParams = Object.keys(params)
            .sort()
            .map(key => `${key}:${params[key]}`)
            .join('|');

        return sortedParams ? `${groupType}|${sortedParams}` : groupType;
    }

    /**
     * Send subscription request
     */
    subscribe(groupType, params) {
        const message = {
            action: 'subscribe',
            group_type: groupType,
            params
        };

        if (this.connected) {
            console.debug(`[WebSocket] → Subscribing to ${groupType}`, params);
            this.sendMessage(message);
        } else {
            console.debug(`[WebSocket] Queued subscription: ${groupType}`, params);
            this.pendingSubscriptions.push(message);
        }
    }

    /**
     * Send unsubscription request
     */
    unsubscribe(groupType, params) {
        const subscriptionKey = this.getSubscriptionKey(groupType, params);

        const message = {
            action: 'unsubscribe',
            group_type: groupType,
            params
        };

        if (this.connected) {
            console.debug(`[WebSocket] → Unsubscribing from ${groupType}`, params);
            this.sendMessage(message);
        }

        this.subscriptions.delete(subscriptionKey);
    }

    /**
     * Send a message to the WebSocket server
     */
    sendMessage(message) {
        const wsElement = document.querySelector('[ws-connect]');
        
        if (!wsElement) {
            console.error('[WebSocket] ws-connect element not found');
            return;
        }

        try {
            const messageStr = JSON.stringify(message);
            this.socket.send(messageStr);
        } catch (e) {
            console.error('[WebSocket] Error while sending:', e);
        }
    }

    /**
     * Process queued subscriptions
     */
    processPendingSubscriptions() {
        if (this.pendingSubscriptions.length === 0) return;

        console.debug(`[WebSocket] Processing ${this.pendingSubscriptions.length} pending subscription(s)`);

        while (this.pendingSubscriptions.length > 0) {
            const message = this.pendingSubscriptions.shift();
            this.sendMessage(message);
        }
    }

    /**
     * Attempt automatic reconnection
     */
    attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('[WebSocket] Maximum reconnect attempts reached');
            showToast('Connection lost. Please reload the page.', 'danger');
            return;
        }

        this.reconnectAttempts++;
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

        console.debug(`[WebSocket] Reconnecting in ${delay}ms (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

        setTimeout(() => {
            console.debug('[WebSocket] Reconnection attempt...');
        }, delay);
    }

    /**
     * Observe DOM changes to detect new elements
     */
    observeDOMChanges() {
        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {

                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return;

                    if (node.hasAttribute && node.hasAttribute('data-ws-subscribe')) {
                        this.subscribeElement(node);
                    }

                    if (node.querySelectorAll) {
                        const elements = node.querySelectorAll('[data-ws-subscribe]');
                        elements.forEach(el => this.subscribeElement(el));
                    }
                });

                mutation.removedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return;

                    if (node.hasAttribute && node.hasAttribute('data-ws-subscribe')) {
                        this.unsubscribeElement(node);
                    }

                    if (node.querySelectorAll) {
                        const elements = node.querySelectorAll('[data-ws-subscribe]');
                        elements.forEach(el => this.unsubscribeElement(el));
                    }
                });
            });
        });

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });

        console.debug('[WebSocket] DOM observer active');
    }

    /**
     * Start heartbeat to keep connection alive
     */
    startHeartbeat() {
        setInterval(() => {
            if (this.connected) {
                this.sendMessage({ action: 'ping' });
            }
        }, 30000); // Ping every 30 seconds
    }

    /**
     * Destroy manager (cleanup)
     */
    destroy() {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
        this.subscriptions.clear();
        this.pendingSubscriptions = [];
        console.debug('[WebSocket] Manager destroyed');
    }
}

export default WebSocketManager;